import {
  CLUB_BENEFITS_PROPAGATION_DELAY_MS,
  clearOgProductsLocalStorage,
  clearSwappableGroupsLocalStorage,
  fetchOGGraphQL,
  fetchRestSubscriptionsList,
  hydrateOgProductsFromLocalStorage,
  hydrateSwappableGroupsFromLocalStorage,
  sortOGOrders,
  SUBSCRIBER_CACHE_TTL_MS,
} from './ordergroove-api-helper';
import { parseOgPlaceDateUtc } from '../../utils/timezone-helpers.js';

const CACHE_VERSION = 4;
const CACHE_KEY_PREFIX = 'dsc_subscriber_cache_';
const CLUB_BENEFITS_PENDING_KEY = 'dsc_club_benefits_pending_until';
const OG_WAIT_TIMEOUT_MS = 8000;
const OG_WAIT_INTERVAL_MS = 100;

let _hydrationPromise = null;
/** True if any caller passed `force: true` while a hydration was already in-flight. */
let _pendingForceAfterCurrentHydration = false;
/**
 * The promise we attached a single post-hydration follow-up to. Prevents stacking N `.finally()`
 * handlers (which caused unbounded work / apparent infinite logs).
 */
let _hydrationFollowUpAttachedTo = null;

const scheduleSingleForceRefreshAfterCurrentHydration = () => {
  if (!_hydrationPromise || _hydrationFollowUpAttachedTo === _hydrationPromise) return;
  const captured = _hydrationPromise;
  _hydrationFollowUpAttachedTo = captured;
  captured.finally(() => {
    if (_hydrationFollowUpAttachedTo === captured) {
      _hydrationFollowUpAttachedTo = null;
    }
    const shouldForceRefresh = _pendingForceAfterCurrentHydration;
    _pendingForceAfterCurrentHydration = false;
    if (shouldForceRefresh) {
      // Defer past the hydration microtask chain so we never synchronously re-enter
      // ensureSubscriberHydrated while the same promise is still settling.
      queueMicrotask(() => {
        void ensureSubscriberHydrated({ force: true });
      });
    }
  });
};

const toNullableString = (value) => (value === undefined || value === null ? null : String(value));
/** Whole-number fields from OG/Shopify; fractional strings truncate (e.g. "1.5" → 1). */
const toIntegerOrNull = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = parseInt(String(value), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const currentCustomerId = () => {
  const customerId = window?.dsc?.customerId;
  if (!customerId || customerId === 'null') return null;
  return String(customerId);
};

const getCacheKey = (customerId) => `${CACHE_KEY_PREFIX}${customerId}`;

const clearAllSubscriberKeys = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    // localStorage unavailable
  }
};

const clearHydratedDscFields = () => {
  window.dsc = window.dsc || {};
  delete window.dsc.subscriberCache;
  delete window.dsc.ogUpcomingOrders;
  delete window.dsc.ogOrdersWithStatus;
  delete window.dsc.ogAllOrderItems;
  delete window.dsc.ogSubscriptions;
  delete window.dsc.ogProductsById;
  delete window.dsc.isUserWithSubscription;
  delete window.dsc.activeSubscriptionsCount;
  delete window.dsc.clubBenefitsLoaded;
  delete window.dsc.subscriberDataLoadedAt;
  window.dsc.swappableItemsByGroup = {};
};

export const clearSubscriberCache = (reason = 'manual') => {
  const customerId = currentCustomerId();
  if (customerId) {
    try {
      localStorage.removeItem(getCacheKey(customerId));
    } catch (error) {
      // localStorage unavailable
    }
    clearSwappableGroupsLocalStorage(customerId);
    clearOgProductsLocalStorage(customerId);
  } else {
    clearAllSubscriberKeys();
    clearSwappableGroupsLocalStorage(null);
    clearOgProductsLocalStorage(null);
  }
  clearHydratedDscFields();
  document.dispatchEvent(new CustomEvent('subscriber-data-cleared', { detail: { reason } }));
};

const isCacheExpired = (cache) => {
  if (!cache?.expiresAt) return false;
  const expiresAt = new Date(cache.expiresAt).getTime();
  if (Number.isNaN(expiresAt)) return true;
  return Date.now() >= expiresAt;
};

export const isNextBoxDateStale = (place) => {
  if (!place) return false;
  const orderDate = parseOgPlaceDateUtc(place);
  if (!orderDate || Number.isNaN(orderDate.getTime())) return true;
  // Compare UTC calendar days (OG place is UTC). Local midnight via setHours would treat a
  // tomorrow-UTC order as "today" for US-Pacific users and clear cache on every load.
  const orderDayUtc = orderDate.toISOString().slice(0, 10);
  const todayDayUtc = new Date().toISOString().slice(0, 10);
  return orderDayUtc <= todayDayUtc;
};

const shouldClearForQuickAction = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has('og_quick_action');
};

/** Next-box club pricing can lag after a sub action; refetch subscriber snapshot on account only during the wait. PDP/collection can keep using cache. */
const isClubBenefitsSubscriberRefetchRoute = () => {
  try {
    return typeof window !== 'undefined' && window.location.pathname.startsWith('/account');
  } catch {
    return false;
  }
};

const shouldBypassCacheForClubBenefitsPropagation = () => {
  try {
    const raw = localStorage.getItem(CLUB_BENEFITS_PENDING_KEY);
    if (!raw) return false;
    const pendingUntil = parseInt(raw, 10);
    if (Number.isNaN(pendingUntil)) {
      localStorage.removeItem(CLUB_BENEFITS_PENDING_KEY);
      return false;
    }
    const maxExpectedPendingUntil = Date.now() + CLUB_BENEFITS_PROPAGATION_DELAY_MS + 1000;
    if (pendingUntil > maxExpectedPendingUntil) {
      localStorage.removeItem(CLUB_BENEFITS_PENDING_KEY);
      return false;
    }
    if (Date.now() < pendingUntil) {
      return isClubBenefitsSubscriberRefetchRoute();
    }
    localStorage.removeItem(CLUB_BENEFITS_PENDING_KEY);
    clearSubscriberCache('club-benefits-propagation-window-ended');
    return true;
  } catch (error) {
    return false;
  }
};

const readCachedSnapshot = (customerId) => {
  try {
    const raw = localStorage.getItem(getCacheKey(customerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || String(parsed.customerId) !== String(customerId)) return null;
    if (parsed.version !== CACHE_VERSION) return null;
    if (isCacheExpired(parsed)) return null;
    return parsed;
  } catch (error) {
    return null;
  }
};

const writeCachedSnapshot = (snapshot) => {
  try {
    localStorage.setItem(getCacheKey(snapshot.customerId), JSON.stringify(snapshot));
  } catch (error) {
    // localStorage unavailable or quota
  }
};

const waitForOG = async () => {
  if (window?.OG?.store?.getState) return;
  const start = Date.now();
  while (Date.now() - start < OG_WAIT_TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, OG_WAIT_INTERVAL_MS));
    if (window?.OG?.store?.getState) return;
  }
  throw new Error('Ordergroove SDK not ready');
};

const getNodes = (connection) => {
  if (!Array.isArray(connection?.edges)) return [];
  return connection.edges.map((edge) => edge?.node).filter(Boolean);
};

const normalizeGroups = (groups = []) =>
  groups
    .map((group) => ({
      group_type: toNullableString(group?.groupType ?? group?.group_type),
      name: toNullableString(group?.name),
    }))
    .filter((group) => group.group_type && group.name);

const normalizeProduct = (product) => {
  const externalProductId = toNullableString(product?.externalProductId ?? product?.external_product_id);
  if (!externalProductId) return null;

  return {
    id: externalProductId,
    external_product_id: externalProductId,
    name: toNullableString(product?.name),
    sku: toNullableString(product?.sku),
    price: toNullableString(product?.price),
    image_url: toNullableString(product?.imageUrl ?? product?.image_url),
    detail_url: toNullableString(product?.detailUrl ?? product?.detail_url),
    groups: normalizeGroups(product?.groups || []),
  };
};

const firstNonEmptyMoneyString = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const s = String(candidate).trim();
    if (s !== '') return s;
  }
  return null;
};

const sumLineItemTotalPrices = (itemNodes) => {
  if (!Array.isArray(itemNodes)) return null;
  let sum = 0;
  let any = false;
  for (const item of itemNodes) {
    const n = parseFloat(String(item?.totalPrice ?? item?.total_price ?? '').trim());
    if (!Number.isNaN(n)) {
      sum += n;
      any = true;
    }
  }
  return any ? sum.toFixed(2) : null;
};

const formatOrderMoneyTwoDecimals = (value) => {
  const normalized = String(value ?? '')
    .replace(/,/g, '')
    .replace(/^\s*\$\s*/, '')
    .trim();
  const n = parseFloat(normalized);
  if (Number.isNaN(n)) return '0.00';
  return n.toFixed(2);
};

const normalizeOrder = (order) => {
  const itemNodes = order?.items?.nodes;
  const sub_total =
    firstNonEmptyMoneyString(
      order?.subTotal,
      order?.subtotal,
      order?.sub_total,
      sumLineItemTotalPrices(itemNodes),
    ) ?? '0.00';
  const shipping_total =
    firstNonEmptyMoneyString(order?.shippingTotal, order?.shipping_total) ?? '0.00';
  const discount_total =
    firstNonEmptyMoneyString(order?.discountTotal, order?.discount_total) ?? '0.00';
  const tax_total = firstNonEmptyMoneyString(order?.taxTotal, order?.tax_total) ?? '0.00';

  let total = firstNonEmptyMoneyString(order?.total);
  if (total === null) {
    const s = parseFloat(sub_total);
    const sh = parseFloat(shipping_total);
    const t = parseFloat(tax_total);
    const d = parseFloat(discount_total);
    if (!Number.isNaN(s)) {
      const discountAmount = Number.isNaN(d) ? 0 : d;
      total = (s + (Number.isNaN(sh) ? 0 : sh) + (Number.isNaN(t) ? 0 : t) - discountAmount).toFixed(2);
    } else {
      total = '0.00';
    }
  }

  return {
    public_id: toNullableString(order?.publicId ?? order?.public_id),
    place: toNullableString(order?.place),
    status: toIntegerOrNull(order?.status) ?? 0,
    shipping_address: toNullableString(order?.shippingAddress?.publicId ?? order?.shipping_address),
    payment: toNullableString(order?.payment?.publicId ?? order?.payment),
    total: formatOrderMoneyTwoDecimals(total),
    sub_total: formatOrderMoneyTwoDecimals(sub_total),
    shipping_total: formatOrderMoneyTwoDecimals(shipping_total),
    discount_total: formatOrderMoneyTwoDecimals(discount_total),
  };
};

const normalizeItem = (item, fallbackOrderId) => {
  const product = normalizeProduct(item?.product);
  return {
    public_id: toNullableString(item?.publicId ?? item?.public_id),
    order: toNullableString(item?.orderPublicId ?? item?.order ?? fallbackOrderId),
    product: toNullableString(product?.external_product_id),
    subscription: toNullableString(item?.subscription?.publicId ?? item?.subscription),
    quantity: toIntegerOrNull(item?.quantity) ?? 1,
    price: toNullableString(item?.price),
    total_price: toNullableString(item?.totalPrice ?? item?.total_price),
    offer: toNullableString(item?.offerPublicId ?? item?.offer),
  };
};

const normalizeSubscription = (subscription) => {
  const product = normalizeProduct(subscription?.product);
  return {
    public_id: toNullableString(subscription?.publicId ?? subscription?.public_id),
    product: toNullableString(product?.external_product_id),
    live: Boolean(subscription?.live),
    every: toIntegerOrNull(subscription?.every),
    every_period: toIntegerOrNull(subscription?.everyPeriod ?? subscription?.every_period),
    quantity: toIntegerOrNull(subscription?.quantity) ?? 1,
    price: toNullableString(subscription?.price),
    cancelled: toNullableString(subscription?.cancelled),
    currency_code: toNullableString(subscription?.currencyCode ?? subscription?.currency_code),
    frequency_days: toIntegerOrNull(subscription?.frequencyDays ?? subscription?.frequency_days),
    start_date: toNullableString(subscription?.startDate ?? subscription?.start_date),
    shipping_address: toNullableString(subscription?.shippingAddress?.publicId ?? subscription?.shipping_address),
    payment: toNullableString(subscription?.payment?.publicId ?? subscription?.payment),
    offer: toNullableString(
      subscription?.offer?.publicId ?? subscription?.offerPublicId ?? subscription?.offer,
    ),
  };
};

const addProductToMap = (productMap, product) => {
  const normalizedProduct = normalizeProduct(product);
  if (!normalizedProduct) return;
  productMap[normalizedProduct.external_product_id] = normalizedProduct;
};

const sortOrdersByPlace = (orders) => {
  const clone = [...orders];
  sortOGOrders(clone);
  return clone;
};

const mergeSubscriptionOffersFromRest = (normalizedSubscriptions, restPayload) => {
  const results = restPayload?.results;
  if (!Array.isArray(results) || results.length === 0) return normalizedSubscriptions;
  const byPublicId = new Map(results.map((s) => [String(s.public_id), s]));
  return normalizedSubscriptions.map((sub) => {
    const rest = byPublicId.get(String(sub.public_id));
    if (!rest) return sub;
    const offer = toNullableString(rest.offer ?? rest.offer_id);
    if (offer === null) return sub;
    return { ...sub, offer };
  });
};

const normalizeToCacheShape = (data, customerId) => {
  const productMap = {};
  const dashboardOrderNodes = getNodes(data?.dashboardOrders);
  const upcomingOrderNodes = dashboardOrderNodes.filter(
    (node) => (toIntegerOrNull(node?.status) ?? 0) === 1,
  );
  const subscriptionNodes = getNodes(data?.subscriptions);

  const upcomingOrders = sortOrdersByPlace(upcomingOrderNodes.map(normalizeOrder).filter((order) => order.public_id));
  const ordersWithStatus = sortOrdersByPlace(
    dashboardOrderNodes.map(normalizeOrder).filter((order) => order.public_id),
  );

  const allOrderItems = [];
  dashboardOrderNodes.forEach((orderNode) => {
    const orderId = toNullableString(orderNode?.publicId);
    const itemNodes = orderNode?.items?.nodes || [];
    itemNodes.forEach((itemNode) => {
      addProductToMap(productMap, itemNode?.product);
      const normalizedItem = normalizeItem(itemNode, orderId);
      if (normalizedItem.public_id || normalizedItem.order || normalizedItem.product) {
        allOrderItems.push(normalizedItem);
      }
    });
  });

  const upcomingFirstOrderId = upcomingOrders?.[0]?.public_id;
  const nextOrderItems = [];
  const firstUpcomingNode = upcomingOrderNodes.find(
    (orderNode) => toNullableString(orderNode?.publicId) === toNullableString(upcomingFirstOrderId),
  );
  (firstUpcomingNode?.items?.nodes || []).forEach((itemNode) => {
    addProductToMap(productMap, itemNode?.product);
    const normalizedItem = normalizeItem(itemNode, upcomingFirstOrderId);
    if (normalizedItem.public_id || normalizedItem.order || normalizedItem.product) {
      nextOrderItems.push(normalizedItem);
    }
  });

  const subscriptions = subscriptionNodes.map((subscriptionNode) => {
    addProductToMap(productMap, subscriptionNode?.product);
    return normalizeSubscription(subscriptionNode);
  });

  const dedupedAllOrderItems = Object.values(
    allOrderItems.reduce((accumulator, item, index) => {
      const key =
        item.public_id ||
        `${item.order || 'order'}-${item.product || 'product'}-${item.subscription || 'subscription'}-${index}`;
      accumulator[key] = item;
      return accumulator;
    }, {}),
  );

  const now = new Date();
  const expiresAt = new Date(now.getTime() + SUBSCRIBER_CACHE_TTL_MS);

  return {
    version: CACHE_VERSION,
    customerId,
    cachedAt: now.toISOString(),
    expiresAt: expiresAt.toISOString(),
    customer: {
      exists: subscriptions.length > 0 || upcomingOrders.length > 0 || ordersWithStatus.length > 0,
    },
    upcomingOrders,
    nextOrderItems,
    subscriptions,
    ordersWithStatus,
    allOrderItems: dedupedAllOrderItems,
    products: productMap,
  };
};

const hydrateWindowDscFromCache = (cache, source = 'cache') => {
  window.dsc = window.dsc || {};
  window.dsc.subscriberCache = cache;
  window.dsc.ogUpcomingOrders = {
    count: cache.upcomingOrders.length,
    results: cache.upcomingOrders,
  };
  window.dsc.ogOrdersWithStatus = {
    count: cache.ordersWithStatus.length,
    results: cache.ordersWithStatus,
  };
  window.dsc.ogAllOrderItems = {
    count: cache.allOrderItems.length,
    results: cache.allOrderItems,
  };
  window.dsc.ogSubscriptions = {
    count: cache.subscriptions.length,
    results: cache.subscriptions,
  };
  window.dsc.ogProductsById = cache.products;
  window.dsc.isUserWithSubscription = Boolean(cache.customer?.exists && cache.upcomingOrders.length > 0);
  window.dsc.activeSubscriptionsCount = cache.subscriptions.filter((subscription) => subscription.live).length;
  window.dsc.clubBenefitsLoaded = true;
  window.dsc.subscriberDataLoadedAt = cache.cachedAt;
  window.dsc.swappableItemsByGroup = window.dsc.swappableItemsByGroup || {};
  hydrateSwappableGroupsFromLocalStorage(cache.customerId);
  hydrateOgProductsFromLocalStorage(cache.customerId);

  document.dispatchEvent(
    new CustomEvent('club-benefits-data-ready', {
      detail: {
        activeSubscriptionsCount: window.dsc.activeSubscriptionsCount,
        clubBenefitsSubActionTaken: window.dsc.clubBenefitsSubActionTaken ?? false,
      },
    }),
  );

  document.dispatchEvent(
    new CustomEvent('subscriber-data-ready', {
      detail: {
        source,
      },
    }),
  );
};

const SUBSCRIBER_SNAPSHOT_QUERY = `
query SubscriberSnapshot($customer: String!, $dashboardFirst: Int!, $subscriptionsFirst: Int!) {
  dashboardOrders: orders(customer: $customer, status: [6, 1, 18], first: $dashboardFirst) {
    edges {
      node {
        publicId
        place
        status
        total
        subTotal
        shippingTotal
        discountTotal
        taxTotal
        shippingAddress { publicId }
        payment { publicId }
        items {
          nodes {
            publicId
            orderPublicId
            quantity
            price
            totalPrice
            offerPublicId
            product {
              externalProductId
              sku
              name
              imageUrl
              detailUrl
              price
              groups {
                groupType
                name
              }
            }
            subscription {
              publicId
            }
          }
        }
      }
    }
  }
  subscriptions(customer: $customer, first: $subscriptionsFirst) {
    edges {
      node {
        publicId
        live
        every
        everyPeriod
        quantity
        price
        cancelled
        currencyCode
        frequencyDays
        startDate
        shippingAddress { publicId }
        payment { publicId }
        product {
          externalProductId
          sku
          name
          imageUrl
          detailUrl
          price
          groups {
            groupType
            name
          }
        }
      }
    }
  }
}
`;

const fetchSubscriberSnapshotGraphql = async (customerId) => {
  await waitForOG();
  const [graphqlResult, restResult] = await Promise.allSettled([
    fetchOGGraphQL(SUBSCRIBER_SNAPSHOT_QUERY, {
      customer: customerId,
      dashboardFirst: 200,
      subscriptionsFirst: 200,
    }),
    fetchRestSubscriptionsList(customerId),
  ]);

  if (graphqlResult.status === 'rejected') {
    const reason = graphqlResult.reason;
    throw reason instanceof Error ? reason : new Error(String(reason));
  }

  const snapshot = normalizeToCacheShape(graphqlResult.value, customerId);

  if (restResult.status === 'fulfilled' && restResult.value) {
    snapshot.subscriptions = mergeSubscriptionOffersFromRest(snapshot.subscriptions, restResult.value);
  }

  return snapshot;
};

export const ensureSubscriberHydrated = async ({ force = false } = {}) => {
  if (_hydrationPromise) {
    if (force) {
      _pendingForceAfterCurrentHydration = true;
      scheduleSingleForceRefreshAfterCurrentHydration();
      return _hydrationPromise;
    }
    return _hydrationPromise;
  }

  const customerId = currentCustomerId();
  if (!customerId) {
    clearSubscriberCache('missing-customer');
    return;
  }

  _hydrationPromise = (async () => {
    // Yield once so synchronous mutation → invalidate → ensureSubscriberHydrated chains
    // finish scheduling before we run body work (avoids re-entrancy / log storms).
    await Promise.resolve();
    try {
      if (shouldClearForQuickAction()) {
        clearSubscriberCache('og-quick-action');
      }

      const bypassCacheForClubBenefitsPropagation = shouldBypassCacheForClubBenefitsPropagation();

      if (!force && !bypassCacheForClubBenefitsPropagation) {
        const cached = readCachedSnapshot(customerId);
        if (cached) {
          const nextOrderPlace = cached?.upcomingOrders?.[0]?.place;
          if (isNextBoxDateStale(nextOrderPlace)) {
            clearSubscriberCache('stale-next-box-date');
          } else {
            hydrateWindowDscFromCache(cached, 'cache');
            return;
          }
        }
      }

      const freshSnapshot = await fetchSubscriberSnapshotGraphql(customerId);
      writeCachedSnapshot(freshSnapshot);
      hydrateWindowDscFromCache(freshSnapshot, 'network');
    } finally {
      _hydrationPromise = null;
    }
  })();

  return _hydrationPromise;
};

window.dsc = window.dsc || {};
window.dsc.ensureSubscriberHydrated = ensureSubscriberHydrated;
window.dsc.clearSubscriberCache = clearSubscriberCache;
